import { RegisterOptions, UseFormReturn } from "react-hook-form";
import { HTMLInputTypeAttribute, ReactNode } from "react";
import { AutocompleteControlProps } from "components/form/AutocompleteControl";
import { IAutocompleteValue, NestedKeyOf } from "components/inputs/Autocomplete/types";
import { InputType } from "components/generators/inputsGenerator";
import { AutocompleteValue, SxProps } from "@mui/material";
import { ParsedQs } from "qs";
import { Exact, InputMaybe, OrderType } from "@generated/graphql";
import { CompetencyMapColumnName, ExaminationColumnName, TrialColumnName } from "layout/TableConstructor/constants";
import { LazyQueryExecFunction } from "@apollo/client";
import { TabListTab } from "layout/TabLayout/components/TabList";

export enum RouteEnum {
  CompetencyMap = "CompetencyMap",
  Examination = "Examination",
  Trial = "Trial",
  Candidate = "Candidate",
  Vacancy = "Vacancy",
  VacancyCandidate = "VacancyCandidate",
}

export interface Identifiable {
  id: string | number | unknown;
  [key: string]: string | number | object[] | string[] | unknown | undefined | null;
}

export interface BaseFilterField<AvailableFilters extends object> {
  label: string; // Название фильтра
  rules?: RegisterOptions; // Валидация (например, react-hook-form)
  name: Extract<keyof AvailableFilters, string> | string;
  isHidden?: boolean;
  additionalFilter?: Partial<Record<keyof AvailableFilters, AvailableFilters[keyof AvailableFilters]>>;
  arrayItemParams?: {
    parentFilterName: string;
    name: { key: string; value: string };
    value: { key: string };
  };
}

type TextFilterField<AvailableFilters extends object> = {
  type: InputType.TEXT;
  customFilter?: (value: string | object | undefined) => string | string[] | undefined | number | object;
  defaultValue?: string | number;
  inputType?: HTMLInputTypeAttribute;
  isRange?: boolean;
} & BaseFilterField<AvailableFilters>;

type DateFilterField<AvailableFilters extends object> = {
  type: InputType.DATE;
  customFilter?: (value: string | undefined) => string | undefined;
  defaultValue?: string;
} & BaseFilterField<AvailableFilters>;

export type AutocompleteFilterField<
  AvailableFilters extends object,
  Value extends IAutocompleteValue = IAutocompleteValue,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  TOption extends IAutocompleteValue = Value,
> = {
  type: InputType.AUTOCOMPLETE;
  options: AutocompleteControlProps<Value, Multiple, DisableClearable, FreeSolo, TOption>["options"];
  multiple?: boolean;
  placeholder?: string;
  label?: string;
  optionName?: NestedKeyOf<Value>;
  getOptionLabel?: AutocompleteControlProps<Value>["getOptionLabel"];
  customFilter?: (
    value: AutocompleteValue<TOption, Multiple, DisableClearable, FreeSolo>
  ) => string | string[] | boolean;
  limitTags?: number;
  defaultValue?: object | null | string[] | string | object[];
} & BaseFilterField<AvailableFilters>;

type InputUnionType<AvailableFilters extends object> =
  | TextFilterField<AvailableFilters>
  | DateFilterField<AvailableFilters>
  | AutocompleteFilterField<
      AvailableFilters,
      any, // Value
      boolean, // Multiple
      boolean, // DisableClearable
      boolean, // FreeSolo
      any // TOption
    >;

export type FilterField<AvailableFilters extends object> = InputUnionType<AvailableFilters>;

export type ColumnNameByRoute<Route extends RouteEnum | undefined> = Route extends RouteEnum.CompetencyMap
  ? CompetencyMapColumnName
  : Route extends RouteEnum.Examination
    ? ExaminationColumnName
    : Route extends RouteEnum.Trial
      ? TrialColumnName
      : string;

export interface ColumnConfigBase<
  T extends Identifiable,
  AvailableFilters extends object,
  CollapsingRowName extends keyof T | undefined = undefined,
  Route extends RouteEnum | undefined = undefined,
> {
  title: string;
  renderTitle?: (data: T[]) => ReactNode;
  name: ColumnNameByRoute<Route> | "action";
  render: (data: T) => ReactNode;
  filterName?: keyof AvailableFilters;
  renderCollapse?: CollapsingRowName extends keyof T
    ? NonNullable<T[CollapsingRowName]> extends Array<infer U>
      ? (data: U) => ReactNode
      : never
    : never;
  cellStyle?: SxProps;
}

export type ColumnConfig<
  T extends Identifiable,
  AvailableFilters extends object,
  OrderField extends string,
  CollapsingRowName extends keyof T | undefined = undefined,
  Route extends RouteEnum | undefined = undefined,
> = ColumnConfigBase<T, AvailableFilters, CollapsingRowName, Route> & {
  sortField?: OrderField;
};

export interface IOrderInput<OrderField extends string> {
  order: OrderType;
  field: OrderField;
}

export interface TableQueryVariables<OrderField extends string> {
  page: number;
  limit: number;
  filter?: object;
  order: IOrderInput<OrderField>;
}

export type OnRowClick<T extends Identifiable, CollapsingRowName extends keyof T | undefined = undefined> = (
  row: T,
  collapsingRow?: CollapsingRowName extends keyof T
    ? NonNullable<T[CollapsingRowName]> extends Array<infer U>
      ? NonNullable<T[CollapsingRowName]>[number]
      : never
    : never
) => void;

export type TableConstructorTab = TabListTab & { filter: InputMaybe<object>; emptyPlaceholderText?: string };

export interface TableConstructorProps<
  T extends Identifiable,
  AvailableFilters extends object,
  OrderField extends string,
  CollapsingRowName extends keyof T | undefined = undefined,
  Route extends RouteEnum | undefined = undefined,
> {
  data: T[];
  totalCount: number | undefined;
  loading: boolean;
  columnsConfig: ColumnConfig<T, AvailableFilters, OrderField, CollapsingRowName, Route>[];
  filtersConfig: FilterField<AvailableFilters>[];
  checkIsCollapsingRow?: (data: T) => boolean;

  defaultVisibleColumnList?: (ColumnNameByRoute<Route> | "action")[];

  permanentFilter?: AvailableFilters;
  defaultOrder: IOrderInput<OrderField>;

  getTableConfig?: () => { fields: string[]; filters: string[] };

  exportRoute?: RouteEnum;
  emptyPlaceholderText: string;

  tabs?: TableConstructorTab[];
  defaultTabId?: string;

  initialFetch?: LazyQueryExecFunction<
    any,
    Exact<{
      page: number;
      limit: number;
      filter?: InputMaybe<object>;
      order: IOrderInput<OrderField>;
    }>
  > | null;
  refetch: (variables: TableQueryVariables<OrderField>) => void;

  collapsingRowName?: CollapsingRowName;

  onRowClick?: OnRowClick<T, CollapsingRowName>;
  storageName: string;
  settingsModalTitle: string;

  renderControls?: () => ReactNode;
}

export type TableBodyProps<T extends Identifiable, CollapsingRowName extends keyof T | undefined = undefined> = {
  data: T[];
  collapsingRowName: CollapsingRowName | undefined;
  checkIsCollapsingRow: ((data: T) => boolean) | undefined;
  onRowClick: OnRowClick<T, CollapsingRowName> | undefined;
};

export interface ITableRowProps<T extends Identifiable, CollapsingRowName extends keyof T | undefined = undefined> {
  row: T;
  collapsingRowName: CollapsingRowName | undefined;
  checkIsCollapsingRow: ((data: T) => boolean) | undefined;
  onRowClick: OnRowClick<T, CollapsingRowName> | undefined;
}

export type TableViewProps<T extends Identifiable, CollapsingRowName extends keyof T | undefined = undefined> = {
  data: T[];
  totalCount: number | undefined;
  emptyPlaceholderText: string;
  collapsingRowName: CollapsingRowName | undefined;
  checkIsCollapsingRow: ((data: T) => boolean) | undefined;
  onRowClick: OnRowClick<T, CollapsingRowName> | undefined;
};

export type FilterForm<AvailableFilters extends object> = Record<
  keyof AvailableFilters | string,
  string | null | [] | ParsedQs | (string | ParsedQs)[] | undefined
>;

export interface IFilterProps<AvailableFilters extends object> {
  form: UseFormReturn<FilterForm<AvailableFilters>>;
}
